import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../layout/Layout"
import axios from "../api";
import { 
  Container, 
  Card, 
  CardHeader, 
  CardBody, 
  Row, 
  Col, 
  FormGroup, 
  Label, 
  Input, 
  Button,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "reactstrap";
import { FaSave, FaArrowLeft, FaCheckCircle } from "react-icons/fa";
import { kecamatanOptions } from "./RegisterUsulanFolder/OptionsFilter";

const initialForm = {
  tanggalDiterima: "",
  nomorSurat: "",
  tanggalSurat: "",
  namaPemohonLembagaPengusul: "",
  namaKepalaKeluarga: "",
  nomorKK: "",
  nik: "",
  alamatPemohon: "",
  perihal: "",
  disposisiDari: "",
  diteruskanKepada: "",
  catatan: "",
  status: "Diproses",
  kecamatan: "",
  desaKelurahan: "",
  file: null
};

const RegisterData = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [localDesaOptions, setLocalDesaOptions] = useState([]);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  
  // Konstanta untuk validasi file
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB dalam bytes
  const ALLOWED_FILE_TYPES = ['application/pdf'];

  // Update desa options ketika kecamatan berubah
  useEffect(() => {
    if (form.kecamatan && kecamatanOptions[form.kecamatan]) {
      setLocalDesaOptions(kecamatanOptions[form.kecamatan]);
    } else {
      setLocalDesaOptions([]);
    }
  }, [form.kecamatan]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === "file") {
      const selectedFile = files && files[0] ? files[0] : null;
      
      if (selectedFile) {
        // Validasi tipe file
        if (!ALLOWED_FILE_TYPES.includes(selectedFile.type)) {
          setErrors(prev => ({ 
            ...prev, 
            file: "Hanya file PDF yang diizinkan" 
          }));
          setForm(prev => ({ ...prev, file: null }));
          return;
        }
        
        // Validasi ukuran file
        if (selectedFile.size > MAX_FILE_SIZE) {
          setErrors(prev => ({ 
            ...prev, 
            file: `Ukuran file terlalu besar. Maksimal ${MAX_FILE_SIZE / (1024 * 1024)}MB` 
          }));
          setForm(prev => ({ ...prev, file: null }));
          return;
        }
        
        // Clear error jika file valid
        if (errors.file) {
          setErrors(prev => ({ ...prev, file: "" }));
        }
      }
      
      setForm((prev) => ({ ...prev, file: selectedFile }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
      
      // Reset desaKelurahan jika kecamatan berubah
      if (name === "kecamatan") {
        setForm(prev => ({ ...prev, desaKelurahan: "" }));
      }
    }
    
    // Clear error untuk field ini
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validasi required fields
    if (!form.tanggalDiterima) newErrors.tanggalDiterima = "Tanggal diterima harus diisi";
    if (!form.nomorSurat) newErrors.nomorSurat = "Nomor surat harus diisi";
    if (!form.tanggalSurat) newErrors.tanggalSurat = "Tanggal surat harus diisi";
    if (!form.namaPemohonLembagaPengusul) newErrors.namaPemohonLembagaPengusul = "Nama pemohon harus diisi";
    if (!form.namaKepalaKeluarga) newErrors.namaKepalaKeluarga = "Nama kepala keluarga harus diisi";
    if (!form.nomorKK) newErrors.nomorKK = "Nomor kartu keluarga harus diisi";
    if (!form.nik) newErrors.nik = "NIK harus diisi";
    if (!form.kecamatan) newErrors.kecamatan = "Kecamatan harus dipilih";
    if (!form.desaKelurahan) newErrors.desaKelurahan = "Desa/kelurahan harus dipilih";
    if (!form.alamatPemohon) newErrors.alamatPemohon = "Alamat pemohon harus diisi";
    if (!form.perihal) newErrors.perihal = "Perihal harus dipilih";
    
    if (form.file) {
      if (!ALLOWED_FILE_TYPES.includes(form.file.type)) {
        newErrors.file = "Hanya file PDF yang diizinkan";
      } else if (form.file.size > MAX_FILE_SIZE) {
        newErrors.file = `Ukuran file terlalu besar. Maksimal ${MAX_FILE_SIZE / (1024 * 1024)}MB`;
      }
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    
    // Validasi form
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setSubmitting(false);
      return;
    }

    try {
      // Buat FormData
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== "") {
          fd.append(k, v);
        }
      });

      // Submit data
      await axios.post("/createregisterusulan", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      // Tampilkan modal sukses
      setSuccessModal(true);
      
    } catch (err) {
      console.error('Submit error:', err);
      
      // Handle validation errors
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
      
      setError(
        err.response?.data?.message || 
        "Gagal menyimpan data. Silakan coba lagi."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessModalClose = () => {
    setSuccessModal(false);
    // Redirect ke halaman rekap setelah modal ditutup
    navigate("/rekapdatabantuanrumah", { 
      state: { success: "Data berhasil ditambahkan!" }
    });
  };

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  
  const contentStyle = isMobile 
    ? {} // No margins on mobile
    : { marginLeft: "250px"};

  return (
    <Layout>
         <Row className={`justify-content-center ${isMobile ? "mx-0" : ""}`} style={{ marginLeft: isMobile ? "0" : "280px" }}>
       
            {error && <Alert color="danger">{error}</Alert>}
            
            <form onSubmit={handleSubmit}>
              <Row>
                <h5 className="home-logo-container">Formulir Register Usulan</h5>
                <div className="home-logo-container">
                  <img src="/images/logobaru.svg" alt="Logo Aplikasi" className="home-logo"/>
                </div>
                
                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Tanggal Terima <span className="text-danger">*</span></Label>
                    <Input 
                      type="date" 
                      name="tanggalDiterima" 
                      value={form.tanggalDiterima} 
                      onChange={handleChange}
                      className={errors.tanggalDiterima ? "is-invalid" : ""}
                      required
                    />
                    {errors.tanggalDiterima && (
                      <div className="invalid-feedback">{errors.tanggalDiterima}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Nomor Surat <span className="text-danger">*</span></Label>
                    <Input 
                    type="text"
                      name="nomorSurat" 
                      value={form.nomorSurat} 
                      onChange={handleChange}
                      className={errors.nomorSurat ? "is-invalid" : ""}
                      required
                    />
                    {errors.nomorSurat && (
                      <div className="invalid-feedback">{errors.nomorSurat}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Tanggal Surat <span className="text-danger">*</span></Label>
                    <Input 
                      type="date" 
                      name="tanggalSurat" 
                      value={form.tanggalSurat} 
                      onChange={handleChange}
                      className={errors.tanggalSurat ? "is-invalid" : ""}
                      required
                    />
                    {errors.tanggalSurat && (
                      <div className="invalid-feedback">{errors.tanggalSurat}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Nama Pemohon / Lembaga Pengusul <span className="text-danger">*</span></Label>
                    <Input 
                      name="namaPemohonLembagaPengusul" 
                      value={form.namaPemohonLembagaPengusul} 
                      onChange={handleChange}
                      className={errors.namaPemohonLembagaPengusul ? "is-invalid" : ""}
                      required
                    />
                    {errors.namaPemohonLembagaPengusul && (
                      <div className="invalid-feedback">{errors.namaPemohonLembagaPengusul}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Nama Kepala Keluarga <span className="text-danger">*</span></Label>
                    <Input 
                      name="namaKepalaKeluarga" 
                      value={form.namaKepalaKeluarga} 
                      onChange={handleChange}
                      className={errors.namaKepalaKeluarga ? "is-invalid" : ""}
                      required
                    />
                    {errors.namaKepalaKeluarga && (
                      <div className="invalid-feedback">{errors.namaKepalaKeluarga}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Nomor Kartu Keluarga<span className="text-danger">*</span></Label>
                    <Input 
                      name="nomorKK"
                      type="number" 
                      value={form.nomorKK} 
                      onChange={handleChange}
                      className={errors.nomorKK ? "is-invalid" : ""}
                      required
                    />
                    {errors.nomorKK && (
                      <div className="invalid-feedback">{errors.nomorKK}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>NIK<span className="text-danger">*</span></Label>
                    <Input 
                      name="nik" 
                      type="number"
                      value={form.nik} 
                      onChange={handleChange}
                      className={errors.nik ? "is-invalid" : ""}
                      required
                    />
                    {errors.nik && (
                      <div className="invalid-feedback">{errors.nik}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Kecamatan <span className="text-danger">*</span></Label>
                    <Input 
                      type="select" 
                      name="kecamatan" 
                      value={form.kecamatan || ""} 
                      onChange={handleChange}
                      className={errors.kecamatan ? "is-invalid" : ""}
                      required
                    >
                      <option value="">Pilih Kecamatan</option>
                      {Object.keys(kecamatanOptions).map((kec) => (
                        <option key={kec} value={kec}>
                          {kec}
                        </option>
                      ))}
                    </Input>
                    {errors.kecamatan && (
                      <div className="invalid-feedback">{errors.kecamatan}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Desa/Kelurahan <span className="text-danger">*</span></Label>
                    <Input 
                      type="select" 
                      name="desaKelurahan" 
                      value={form.desaKelurahan || ""} 
                      onChange={handleChange}
                      disabled={!form.kecamatan}
                      className={errors.desaKelurahan ? "is-invalid" : ""}
                      required
                    >
                      <option value="">Pilih Desa/Kelurahan</option>
                      {localDesaOptions.map((desa) => (
                        <option key={desa} value={desa}>
                          {desa}
                        </option>
                      ))}
                    </Input>
                    {errors.desaKelurahan && (
                      <div className="invalid-feedback">{errors.desaKelurahan}</div>
                    )}
                    {!form.kecamatan && (
                      <small className="text-muted">Pilih kecamatan terlebih dahulu</small>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Alamat Pemohon <span className="text-danger">*</span></Label>
                    <Input 
                      type="textarea"
                      name="alamatPemohon" 
                      value={form.alamatPemohon} 
                      onChange={handleChange}
                      className={errors.alamatPemohon ? "is-invalid" : ""}
                      rows="2"
                      required
                    />
                    {errors.alamatPemohon && (
                      <div className="invalid-feedback">{errors.alamatPemohon}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Perihal / Usulan <span className="text-danger">*</span></Label>
                    <Input 
                      type="select" 
                      name="perihal" 
                      value={form.perihal} 
                      onChange={handleChange}
                      className={errors.perihal ? "is-invalid" : ""}
                      required
                    >
                      <option value="">Pilih</option>
                      <option value="Rehab Rumah Tidak Layak Huni">Rehab Rumah Tidak Layak Huni</option>
                      <option value="Pembangunan Rumah Layak Huni">Pembangunan Rumah Layak Huni</option>
                      <option value="Meteran Listrik 900 VA">Meteran Listrik 900 VA</option>
                    </Input>
                    {errors.perihal && (
                      <div className="invalid-feedback">{errors.perihal}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Disposisi Dari</Label>
                    <Input 
                      name="disposisiDari" 
                      value={form.disposisiDari} 
                      onChange={handleChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Diteruskan Kepada</Label>
                    <Input 
                      name="diteruskanKepada" 
                      value={form.diteruskanKepada} 
                      onChange={handleChange}
                    />
                  </FormGroup>
                </Col>

                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Catatan / Tindak Lanjut</Label>
                    <Input 
                      type="textarea" 
                      name="catatan" 
                      value={form.catatan} 
                      onChange={handleChange}
                      rows="3"
                    />
                  </FormGroup>
                </Col>

                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Status</Label>
                    <Input 
                      type="text" 
                      name="status" 
                      value={form.status || "Diproses"} 
                      readOnly 
                      className="bg-light"
                    />
                  </FormGroup>
                </Col>

                <Col md={6} className="mb-3">
                  <FormGroup>
                    <Label>Upload File (PDF, maks 5MB)</Label>
                    <Input 
                      type="file" 
                      name="file" 
                      accept="application/pdf" 
                      onChange={handleChange}
                      className={errors.file ? "is-invalid" : ""}
                    />
                    {errors.file && (
                      <div className="invalid-feedback">{errors.file}</div>
                    )}
                    {form.file && (
                      <div className="mt-1">
                        <small className="text-muted d-block">
                          File terpilih: {form.file.name}
                        </small>
                        <small className="text-muted">
                          Ukuran: {(form.file.size / (1024 * 1024)).toFixed(2)} MB
                        </small>
                      </div>
                    )}
                    <small className="text-muted d-block mt-1">
                      Format: PDF | Maksimal: 5MB
                    </small>
                  </FormGroup>
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button 
                  color="secondary" 
                  type="button"
                  onClick={() => navigate("/rekapdatabantuanrumah")}
                  disabled={submitting}
                >
                  Batal
                </Button>
                <Button 
                  color="primary" 
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Simpan Data
                    </>
                  )}
                </Button>
                <Button 
                  color="secondary" 
                  size="sm"
                  onClick={() => navigate("/rekapdatabantuanrumah")}
                >
                  <FaArrowLeft className="me-2" />
                  Kembali
                </Button>
              </div>
            </form>
        

        {/* Success Modal */}
        <Modal isOpen={successModal} toggle={handleSuccessModalClose} centered>
          <ModalHeader toggle={handleSuccessModalClose} className="border-0 pb-0">
            <div className="d-flex align-items-center">
              <FaCheckCircle className="text-success me-2" size={24} />
              <h5 className="mb-0 text-success">Berhasil!</h5>
            </div>
          </ModalHeader>
          <ModalBody className="text-center">
            <p className="mb-0">Data berhasil disimpan!</p>
          </ModalBody>
          <ModalFooter className="border-0 justify-content-center">
            <Button color="success" onClick={handleSuccessModalClose}>
              Oke
            </Button>
          </ModalFooter>
        </Modal>
      </Row>
    </Layout>
  );
};

export default RegisterData;